import React, { useEffect } from 'react';
import { ZoomIn, X } from 'lucide-react';
import Button from '@ajat/design-system/Button';
import GalleryBack from './GalleryBack';
import './Gallery.css';

const Gallery = ({
  images,
  // Fallback alt text (the product / package name) for images that carry none —
  // the colour photos of the compatibility tree come without one.
  alt = '',
  activeImage,
  setActiveImage,
  isZoomed,
  setIsZoomed,
  scrollYBeforeZoom,
  galleryRef,
  handleTouchStart,
  handleTouchEnd,
}) => {
  // Mobile zoom: the gallery is pinned to the visible viewport (Gallery.css);
  // lock the document scroll so nothing underneath moves — a page scroll would
  // also collapse/expand the browser toolbars under the overlay.
  useEffect(() => {
    if (!isZoomed || typeof document === 'undefined') return undefined;
    const { body, documentElement: html } = document;
    const prev = { body: body.style.overflow, html: html.style.overflow };
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev.body;
      html.style.overflow = prev.html;
    };
  }, [isZoomed]);

  return (
    <div className="pp__gallery pp__gallery--photo">
      <GalleryBack />

      {/* Thumbnails — desktop */}
      <div className="pp__thumbs">
        {images.map((img, i) => (
          <button
            key={i}
            className={`pp__thumb ${i === activeImage ? 'pp__thumb--active' : ''}`}
            onClick={() => setActiveImage(i)}
            aria-label={`View image ${i + 1}`}
          >
            <img src={img.src} alt={img.alt || alt} />
          </button>
        ))}
      </div>

      {/* Main image carousel */}
      <div
        className="pp__image-container"
        ref={galleryRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="pp__image-track"
          style={{ transform: `translateX(-${activeImage * 100}%)` }}
        >
          {images.map((img, i) => (
            <div className="pp__image-slide" key={i}>
              <img src={img.src} alt={img.alt || alt} className="pp__image" draggable={false} />
            </div>
          ))}
        </div>
      </div>

      {/* Bullets — mobile */}
      <div className="pp__bullets">
        {images.map((_, i) => (
          <button
            key={i}
            className={`pp__bullet ${i === activeImage ? 'pp__bullet--active' : ''}`}
            onClick={() => setActiveImage(i)}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>

      {/* Zoom — mobile */}
      {!isZoomed && (
        <div className="pp__zoom-btn-container">
          <Button
            size="tertiary"
            variant="white"
            iconOnly
            iconLeft={<ZoomIn size={20} />}
            onClick={() => {
              scrollYBeforeZoom.current = window.scrollY;
              setIsZoomed(true);
              window.scrollTo(0, 0);
            }}
            aria-label="Zoom in"
          />
        </div>
      )}
      {isZoomed && (
        <div className="pp__zoom-close-container">
          <Button
            size="tertiary"
            variant="gray"
            iconOnly
            iconLeft={<X size={20} />}
            onClick={() => {
              setIsZoomed(false);
              requestAnimationFrame(() => window.scrollTo(0, scrollYBeforeZoom.current));
            }}
            aria-label="Close zoom"
          />
        </div>
      )}
    </div>
  );
};

export default Gallery;
